import { CSSProperties, JSX, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog_VM,
  DialogFrame,
  useDialog,
} from "../../../components/Dialogs/dialog-base";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { useWhiteLabelContext } from "../../../../WhiteLabelContext";
import LoadingElement from "../../../components/loading-element/loading-element";
import { useCartContext } from "../../../../contexts/CartContext";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import {
  CartItemDesign,
  CartItemLogo,
} from "../../order-create/models/CartItem";
import { PartlyHeatingList } from "./partly-heating-list";
import { IdLookUps } from "../../../../utils/models/idLookUp";
import {
  BaseLogo,
  getProductPrice,
} from "../../../../core/models/entities/BaseLogo";
import { PriceLineAsString } from "../../../components/Logo/LogoPrices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faForward,
  faRightLong,
} from "@fortawesome/free-solid-svg-icons";
import { Role } from "../../../../core/models/entities/Role";
import { orderLineTitleText } from "../../order-details/subcomponents/OrderLineData";

interface Props {
  component: JSX.Element;
  open: () => void;
}


// NAV heating names repeat the word ("Heating Sweatshirt") right after the
// Heating/Påvarmning label - strip the redundant prefix for display only.
function heatingDisplayName(name?: string): string {
  return (name ?? "").replace(/^heating\s+/i, "");
}

export enum HeatingOption {
  all,
  partly,
  none,
}

export function useAddHeatingFeeOrderDialog({
  onContinue,
  onCancel,
}: {
  onContinue: (
    heatingOption: HeatingOption,
    extraLogos: ExtraLogo[],
  ) => Promise<void>;
  onCancel?: () => void;
}): Props {
  const { designs, logos } = useCartContext();
  const [loading, setLoading] = useState(false);
  const [heatingOption, setHeatingOption] = useState<HeatingOption>(
    HeatingOption.all,
  );

  const getExtraLogo = (item: CartItemLogo): ExtraLogo => ({
    logo: item.baseLogo,
    // NOTE: toStorage and toDelivery quantities may not represent all logo type quantities,
    //  but we only let image logos through this dialog, and they only use these two fields.
    cartQuantity: item.toDelivery + item.toStorage,
    quantity: 0,
    option: "toDelivery",
  });

  const currentCartLogos = useMemo(() => {
    const result: IdLookUps<ExtraLogo> = {};

    // Sum quantities from designs — each design instance contributes its quantity
    designs.forEach((item) => {
      const designQuantity = item.instances.reduce(
        (sum, inst) => sum + inst.toDelivery + inst.toStorage,
        0,
      );

      item.design.designLogos.forEach((designLogo) => {
        const logo = designLogo.logo;

        // Only include logos with transfer type "transfer"
        if (logo.transferMethod.type.toLowerCase() !== "transfer") {
          return;
        }

        if (result[logo.id]) {
          result[logo.id].cartQuantity += designQuantity;
        } else {
          result[logo.id] = {
            logo: designLogo.logo,
            cartQuantity: designQuantity,
            quantity: 0,
            option: "toDelivery",
          };
        }
      });
    });

    // Add standalone logo quantities (accumulate if logo already counted from designs)
    logos.forEach((l) => {
      if (l.baseLogo.transferMethod.type.toLowerCase() === "transfer") {
        const logoQuantity = l.toDelivery + l.toStorage;
        if (result[l.logoId]) {
          result[l.logoId].cartQuantity += logoQuantity;
        } else {
          result[l.logoId] = getExtraLogo(l);
        }
      }
    });

    return result;
  }, [designs, logos]);

  const [extraLogos, setExtraLogos] = useState<IdLookUps<ExtraLogo>>(currentCartLogos);

  // Sync with cart: remove logos no longer in the cart, add new ones,
  // preserve user-set quantity/option but always refresh cartQuantity.
  useEffect(() => {
    setExtraLogos((prev) => {
      const synced: IdLookUps<ExtraLogo> = {};
      for (const id of Object.keys(currentCartLogos)) {
        const existing = prev[id];
        if (existing) {
          synced[id] = { ...existing, cartQuantity: currentCartLogos[id].cartQuantity };
        } else {
          synced[id] = currentCartLogos[id];
        }
      }
      return synced;
    });
  }, [currentCartLogos]);

  function updateExtraLogo(extraLogo: ExtraLogo): void {
    setExtraLogos((state) => {
      return {
        ...state,
        [extraLogo.logo.id]: extraLogo,
      };
    });
  }

  const dialog = useDialog();

  function handleonApproveAndContinue() {
    setLoading(true);
    const logos = Object.values(extraLogos) ?? [];
    onContinue(heatingOption, logos).finally(() => {
      setLoading(false);
      dialog.closeDialog();
    });
  }

  function handleOnAll() {
    setHeatingOption(HeatingOption.all);
  }

  function handleOnPartly() {
    setHeatingOption(HeatingOption.partly);
  }

  function handleOnNone() {
    setHeatingOption(HeatingOption.none);
  }

  function handleCancel() {
    if (onCancel) onCancel();
    dialog.closeDialog();
  }

  return {
    component: (
      <>
        <AddHeatingFeeOrderDialog
          dialogVm={dialog}
          selectedHeatingOption={heatingOption}
          loading={loading}
          onApproveAndContinue={handleonApproveAndContinue}
          onAll={handleOnAll}
          onPartly={handleOnPartly}
          onNone={handleOnNone}
          onCancel={handleCancel}
          designs={designs}
          extraLogos={Object.values(extraLogos) ?? []}
          setExtraLogos={updateExtraLogo}
        />
      </>
    ),
    open: dialog.openDialog,
  };
}

interface AddHeatingFeeOrderDialogProps {
  dialogVm: Dialog_VM;
  selectedHeatingOption: HeatingOption;
  onApproveAndContinue: () => void;
  onAll: () => void;
  onPartly: () => void;
  onNone: () => void;
  onCancel?: () => void;
  loading: boolean;
  designs: CartItemDesign[];
  extraLogos: ExtraLogo[];
  setExtraLogos: (extraLogo: ExtraLogo) => void;
}

export interface ExtraLogo {
  logo: BaseLogo;
  cartQuantity: number;
  quantity: number;
  option: ExtraLogoOption;
}

export type ExtraLogoOption = "toDelivery" | "toStorage";

export function AddHeatingFeeOrderDialog({
  onApproveAndContinue,
  onAll,
  onPartly,
  onNone,
  onCancel,
  loading,
  dialogVm,
  selectedHeatingOption,
  extraLogos,
  setExtraLogos,
  designs,
}: AddHeatingFeeOrderDialogProps) {
  const { t } = useTranslation();
  const { role } = useProfileContext();
  const isCustomer = role === Role.EndUser;

  const heatingComponent = designs
    .reduce<CartItemDesign[]>((unique, d) => {
      return unique.find((u) => u.id == d.id) ? unique : [...unique, d];
    }, [])
    .filter((design) => {
      // Only include designs that have at least one logo with transfer type "transfer"
      return design.design.designLogos.some(
        (designLogo) =>
          designLogo.logo.transferMethod.type.toLowerCase() === "transfer",
      );
    })
    .map((x) => {
      if (x.design.heating?.prices === undefined) {
        return (
          <p key={x.id}>
            {t("orderConfirmation.heating")}{" "}
            {orderLineTitleText(heatingDisplayName(x.design.heating?.name), t)}
          </p>
        );
      }
      // Match the confirm-order page's heating-quantity formula
      // (useOrderActionsHandler buildLocalOrderDraft heating-line section): heating is
      // applied per non-embroidery logo per produced design, so qty is
      // Σ((toDelivery + toStorage) × non-embroidery-logo-count) across all instances.
      const heatingQty = x.instances.reduce((total, inst) => {
        const instQty = (inst.toDelivery ?? 0) + (inst.toStorage ?? 0);
        if (instQty <= 0) return total;
        const instanceMultiplier = inst.logos.filter((l) => {
          const designLogo = x.design.designLogos?.find(
            (d) => d.logoId === l.logoId,
          );
          const baseLogo = designLogo?.logo ?? l.baseLogo;
          const type = baseLogo?.transferMethod?.type?.toLowerCase() ?? "";
          return (
            type !== "broderi" &&
            type !== "embroidery" &&
            type !== "direk tryk" &&
            type !== "direkte tryk"
          );
        }).length;
        return total + instQty * instanceMultiplier;
      }, 0);
      const price = getProductPrice(x.design.heating!.prices!, heatingQty)!;
      const displayUnitPrice =
        isCustomer && price.adjustedCustomerPrice != null
          ? price.adjustedCustomerPrice
          : price.adjustedPrice;
      const priceText = PriceLineAsString({
        price: { ...price, adjustedPrice: displayUnitPrice },
        excludeQuantity: true,
      });
      return (
        <div key={x.id}>
          <div className="flex justify-between w-full items-start">
            <div className="text-xs p-1 flex flex-col">
              <p>
                <b>{t("orderConfirmation.product")}</b> {x.design.product?.mul}
              </p>
              <p>
                <b>{t("orderConfirmation.heating")}</b>{" "}
                {orderLineTitleText(heatingDisplayName(x.design.heating?.name), t)}
              </p>
            </div>
            <div className="flex-1 text-xs p-1 text-right">
              {priceText} <br /> {t("orderConfirmation.perHeating")}
            </div>
          </div>
          {designs.indexOf(x) < designs.length - 1 && (
            <hr className="border-gray-200 my-1" />
          )}
        </div>
      );
    });

  const { whiteLabel } = useWhiteLabelContext();

  function getSelectedStyle(
    selected: HeatingOption,
    heatingOption: HeatingOption,
  ): CSSProperties {
    return heatingOption === selected
      ? {
          backgroundColor: whiteLabel.colorHex,
          color: "#F6FAF6",
        }
      : {};
  }

  if (loading) return null;

  return (
    <DialogFrame dialogOption={dialogVm}>
      {loading && <LoadingElement transparent />}
      <div className={"flex flex-col p-4"}>
        <p className={"text-left text-base font-bold pb-2"}>
          {t("orderConfirmation.heatingFees")}
        </p>
        <p className={"text-left pb-1"}>
          {t("orderConfirmation.heatingFeesIntro")}
        </p>
        <div className={"flex flex-col justify-center space-y-4"}>
          <div className="bg-gray-100 rounded mb-2 p-1">{heatingComponent}</div>
          <p className={"text-left"}>
            {t("orderConfirmation.chooseTransfersApplied")}
          </p>
          <div className="flex flex-row justify-between space-x-10">
            <div className="flex flex-row">
              <SecondaryButton
                style={getSelectedStyle(
                  selectedHeatingOption,
                  HeatingOption.all,
                )}
                className={`rounded-r-none font-bold border-r-0`}
                onClick={onAll}
              >
                {t("orderConfirmation.all")}
              </SecondaryButton>
              <SecondaryButton
                style={getSelectedStyle(
                  selectedHeatingOption,
                  HeatingOption.none,
                )}
                className={`rounded-l-none font-bold border-l-0 ${getSelectedStyle(
                  selectedHeatingOption,
                  HeatingOption.none,
                )}`}
                onClick={onNone}
              >
                {t("orderConfirmation.none")}
              </SecondaryButton>
            </div>
            <div>
              <PrimaryButton
                className="font-bold"
                onClick={onApproveAndContinue}
              >
                {t("confirm")}
              </PrimaryButton>
            </div>
          </div>
          <hr className="border-gray-200 my-2" />
          <PartlyHeatingList
            extraLogos={extraLogos}
            setExtraLogos={setExtraLogos}
          />
        </div>
      </div>
    </DialogFrame>
  );
}
